/**
 * Blog Analytics System
 * Handles reading time calculation, view tracking, and trending detection
 */

class BlogAnalytics {
  constructor() {
    this.storageKey = 'blogAnalytics';
    this.viewsKey = 'articleViews';
    this.init();
  }

  init() {
    this.calculateReadingTimes();
    this.trackArticleView();
    this.updateViewCounts();
    this.identifyTrendingArticles();
  }

  /**
   * Calculate reading time for all articles on the page
   */
  calculateReadingTimes() {
    const articles = document.querySelectorAll('[data-article-content]');
    
    articles.forEach(article => {
      const content = article.getAttribute('data-article-content');
      const wordCount = this.countWords(content);
      const readingTime = this.calculateReadingTime(wordCount);
      
      // Update reading time display
      const readingTimeElement = article.querySelector('[data-reading-time]');
      if (readingTimeElement) {
        readingTimeElement.textContent = `${readingTime} min read`;
      }
    });
  }

  /**
   * Count words in content
   */
  countWords(content) {
    if (!content) return 0;
    return content.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  /**
   * Calculate reading time based on word count
   * Average reading speed: 200 words per minute
   */
  calculateReadingTime(wordCount) {
    const wordsPerMinute = 200;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return Math.max(1, minutes); // Minimum 1 minute
  }

  /**
   * Track article view in localStorage
   */
  trackArticleView() {
    // Check if we're on an article page
    const articleId = this.getCurrentArticleId();
    if (!articleId) return;

    const views = this.getArticleViews();
    const now = Date.now();

    if (!views[articleId]) {
      views[articleId] = {
        count: 0,
        firstView: now,
        lastView: now,
        views: []
      };
    }

    // Add new view
    views[articleId].count++;
    views[articleId].lastView = now;
    views[articleId].views.push(now);

    // Keep only last 100 views to prevent storage bloat
    if (views[articleId].views.length > 100) {
      views[articleId].views = views[articleId].views.slice(-100);
    }

    this.saveArticleViews(views);
  }

  /**
   * Get current article ID from URL or data attribute
   */
  getCurrentArticleId() {
    // Try to get from meta tag
    const metaArticle = document.querySelector('meta[property="og:url"]');
    if (metaArticle) {
      const url = metaArticle.getAttribute('content');
      const match = url.match(/\/blogs\/[^\/]+\/(.+)/);
      if (match) return match[1];
    }

    // Try to get from body data attribute
    const body = document.querySelector('body');
    if (body && body.hasAttribute('data-article-id')) {
      return body.getAttribute('data-article-id');
    }

    // Try to get from URL
    const path = window.location.pathname;
    const match = path.match(/\/blogs\/[^\/]+\/(.+)/);
    if (match) return match[1];

    return null;
  }

  /**
   * Get all article views from localStorage
   */
  getArticleViews() {
    try {
      const data = localStorage.getItem(this.viewsKey);
      return data ? JSON.parse(data) : {};
    } catch (e) {
      console.error('Error reading article views:', e);
      return {};
    }
  }

  /**
   * Save article views to localStorage
   */
  saveArticleViews(views) {
    try {
      localStorage.setItem(this.viewsKey, JSON.stringify(views));
    } catch (e) {
      console.error('Error saving article views:', e);
    }
  }

  /**
   * Update view count displays on article cards
   */
  updateViewCounts() {
    const articleCards = document.querySelectorAll('[data-article-id]');
    const views = this.getArticleViews();

    articleCards.forEach(card => {
      const articleId = card.getAttribute('data-article-id');
      const viewCountElement = card.querySelector('[data-view-count]');
      
      if (viewCountElement && views[articleId]) {
        const count = views[articleId].count;
        viewCountElement.textContent = this.formatViewCount(count);
        viewCountElement.style.display = count > 0 ? '' : 'none';
      }
    });
  }

  /**
   * Format view count for display
   */
  formatViewCount(count) {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k views`;
    }
    return `${count} view${count !== 1 ? 's' : ''}`;
  }

  /**
   * Identify trending articles based on recent views
   */
  identifyTrendingArticles() {
    const views = this.getArticleViews();
    const now = Date.now();
    const weekAgo = now - (7 * 24 * 60 * 60 * 1000); // 7 days ago
    const trending = [];

    // Calculate trending score for each article
    Object.keys(views).forEach(articleId => {
      const article = views[articleId];
      const recentViews = article.views.filter(timestamp => timestamp >= weekAgo);
      
      if (recentViews.length >= 3) { // Minimum 3 views to be trending
        const velocity = recentViews.length / 7; // Views per day
        trending.push({
          id: articleId,
          score: velocity,
          recentViews: recentViews.length,
          totalViews: article.count
        });
      }
    });

    // Sort by trending score
    trending.sort((a, b) => b.score - a.score);

    // Add trending badges to top articles
    this.addTrendingBadges(trending.slice(0, 5)); // Top 5 trending

    return trending;
  }

  /**
   * Add trending badges to article cards
   */
  addTrendingBadges(trendingArticles) {
    trendingArticles.forEach(article => {
      const cards = document.querySelectorAll(`[data-article-id="${article.id}"]`);
      
      cards.forEach(card => {
        const badgeContainer = card.querySelector('[data-trending-badge]');
        if (badgeContainer && !badgeContainer.querySelector('.trending-badge')) {
          badgeContainer.innerHTML = `
            <span class="trending-badge">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 7l2-2 2 2 3-3 2 2 2-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Trending
            </span>
          `;
          badgeContainer.style.display = '';
        }
      });
    });
  }

  /**
   * Get analytics data for an article
   */
  getArticleAnalytics(articleId) {
    const views = this.getArticleViews();
    return views[articleId] || null;
  }

  /**
   * Clear old analytics data (older than 90 days)
   */
  clearOldData() {
    const views = this.getArticleViews();
    const now = Date.now();
    const ninetyDaysAgo = now - (90 * 24 * 60 * 60 * 1000);
    let updated = false;

    Object.keys(views).forEach(articleId => {
      if (views[articleId].lastView < ninetyDaysAgo) {
        delete views[articleId];
        updated = true;
      }
    });

    if (updated) {
      this.saveArticleViews(views);
    }
  }
}

// Initialize analytics when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.blogAnalytics = new BlogAnalytics();
    
    // Clean old data once per session
    if (!sessionStorage.getItem('analyticsCleared')) {
      window.blogAnalytics.clearOldData();
      sessionStorage.setItem('analyticsCleared', 'true');
    }
  });
} else {
  window.blogAnalytics = new BlogAnalytics();
  
  // Clean old data once per session
  if (!sessionStorage.getItem('analyticsCleared')) {
    window.blogAnalytics.clearOldData();
    sessionStorage.setItem('analyticsCleared', 'true');
  }
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BlogAnalytics;
}



